import { ReactNode, useState } from 'react';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import FileUpload from '@/components/FileUpload';
import Badge from '@/design_system/Badge';
import FoldableSection from '@/design_system/FoldableSection';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { PhotoCardGrid } from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import InlineText from '@/design_system/Typography/InlineText';
import IconStore from '@/icons/Store.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import {
  ArticleConditionForm,
  ArticleConditionFormType,
  TEMPORARY_ID_PREFIX,
  useUpdateArticleCondition,
} from '@/models/article';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { useActionsQuestionErrors } from './ActionQuestions';
import { ConditionFormQuestion, getSubQuestion, useQuestionErrors } from './ConditionQuestion';

export const ConditionContextAndPhotos = ({
  question,
  action,
  type,
  articleConditionForms,
}: {
  question?: ConditionFormQuestion;
  action?: { id: string; stableId: string; name: string };
  type: ArticleConditionFormType;
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { article, view } = useArticleContext();
  const { isMobile } = useViewPort();
  const questionsErrors = useQuestionErrors({ question, type });
  const actionsQuestionsErrors = useActionsQuestionErrors({ actionId: action?.id });

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const hasBeenFullyAnsweredWithNoDefects = hasQuestionBeenFullyAnsweredWithNoDefects({
    question,
    action,
    articleDefects: article.snapshot.articleDefects,
    articleConditionForm: editableArticleConditionForm,
  });

  const editableArticleCondition = editableArticleConditionForm?.articleConditions.find(
    (articleCondition) =>
      question
        ? articleCondition.questionId === question.id
        : articleCondition.articleActionId === action?.id
  );

  const editable =
    !!editableArticleCondition &&
    (type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable);

  const context = editableArticleCondition?.context;

  const conditionPhotos = articleConditionForms.flatMap(
    (articleConditionForm) =>
      articleConditionForm.articleConditions
        ?.find((articleCondition) =>
          question
            ? articleCondition.questionId === question.id
            : articleCondition.articleActionId === action?.id ||
              articleCondition.articleAction?.stableId === action?.stableId
        )
        ?.media.map((medium) => ({
          ...medium,
          customIcon:
            articleConditionForm.completedAt && type === 'quality-control' ? (
              articleConditionForm.store ? (
                <Badge color="secondary" size="large" iconOnly>
                  <IconStore />
                </Badge>
              ) : articleConditionForm.workshop ? (
                <Badge color="secondary" size="large" iconOnly>
                  <IconStoreRepair />
                </Badge>
              ) : undefined
            ) : undefined,
        })) ?? []
  );

  if (!editable) {
    if (!conditionPhotos.length) {
      return <div style={{ paddingTop: '12px' }} />;
    }

    return (
      <Stack padding="8px 12px 12px 12px" gap="0.5rem">
        <PhotoCardGrid media={conditionPhotos} size="medium" />
      </Stack>
    );
  }

  if (!hasBeenFullyAnsweredWithNoDefects) {
    return <div style={{ paddingTop: '12px' }} />;
  }

  const actionName = action?.name;
  const questionTitle = question
    ? _db(question.title)
    : t({
        id: 'requests.articles.quality-control.actions-questions.title',
        message: `Has the action "${actionName}" been done properly?`,
      });
  const contextAndPhotosAriaLabel = t({
    id: 'requests.new.articles.drawer.conditions-form.context.context-and-photos',
    message: `Context and photos for: ${questionTitle}`,
  });

  const isBlockingAnswer = action
    ? editableArticleCondition?.answerData?.yesNoRadioValue === 'no'
    : !!question?.answers?.find((answer) => editableArticleCondition?.answerId === answer.id)
        ?.makesQualityControlKO;

  const arePhotosRequired =
    type === 'initial-condition'
      ? view.initialCondition.photo.required
      : isBlockingAnswer && view.qualityControl.photos.required;
  const isContextRequired =
    type === 'initial-condition' ? view.initialCondition.context.required : isBlockingAnswer;

  const labelPhoto = (
    <>
      <Trans id="requests.new.articles.drawer.conditions-form.context.column.photos">Photos</Trans>
      {!arePhotosRequired && <OptionalLabel />}
    </>
  );

  const labelContext = (
    <>
      <Trans id="requests.new.articles.drawer.conditions-form.context.column.context">
        Context
      </Trans>
      {!isContextRequired && <OptionalLabel />}
    </>
  );

  const isFoldable =
    type === 'initial-condition' ? !arePhotosRequired && !isContextRequired : !isBlockingAnswer;

  const photosAndContextContent = (
    <Stack
      row={!isMobile}
      gap="1rem"
      padding={isFoldable ? undefined : '12px'}
      alignItems={isMobile ? undefined : 'flex-start'}
      ariaLabel={contextAndPhotosAriaLabel}
    >
      <div style={{ flex: 1 }}>
        <FileUpload
          label={labelPhoto}
          ariaLabel={t({
            id: 'requests.new.articles.drawer.conditions-form.context.column.photos',
            message: 'Photos',
          })}
          media={conditionPhotos}
          uploadData={{
            type: 'condition-photo',
            articleId: article.id,
            articleConditionId: editableArticleCondition?.id,
          }}
          style={{ flex: '1' }}
          size="medium"
          variant="grid"
          deleteWithApi
          withQrCode
          secondaryActionIconOnly={!isMobile}
          type="photo"
          isInvalid={questionsErrors?.missingPhoto || actionsQuestionsErrors?.missingPhoto}
          showDisabledInput={!!editableArticleCondition?.id.startsWith(TEMPORARY_ID_PREFIX)}
        />
      </div>
      <div style={{ flex: 1 }}>
        <ContextInput
          key={editableArticleCondition?.id}
          label={labelContext}
          question={question}
          action={action}
          currentContext={context}
          type={type}
          isInvalid={questionsErrors?.missingContext || actionsQuestionsErrors?.missingContext}
        />
      </div>
    </Stack>
  );

  if (!isFoldable) {
    return photosAndContextContent;
  }

  const photosCount = conditionPhotos.length;
  const hasContext = !!context?.trim();
  const hasAnyContent = photosCount > 0 || hasContext;

  const photosSummary =
    photosCount > 0
      ? t({
          id: 'requests.new.articles.drawer.conditions-form.context.summary.photos-count',
          message: plural(photosCount, {
            one: `${photosCount} photo`,
            other: `${photosCount} photos`,
          }),
        })
      : t({
          id: 'requests.new.articles.drawer.conditions-form.context.summary.no-photos',
          message: 'No photos',
        });

  const contextSummary = hasContext
    ? t({
        id: 'requests.new.articles.drawer.conditions-form.context.summary.context-added',
        message: 'Context added',
      })
    : t({
        id: 'requests.new.articles.drawer.conditions-form.context.summary.no-context',
        message: 'No context',
      });

  return (
    <Stack padding="12px">
      <FoldableSection
        size="small"
        withHeaderRightSeparator
        startClosed={true}
        label={
          <InlineText color="secondary">
            {t({
              id: 'requests.new.articles.drawer.conditions-form.context.foldable.label',
              message: 'Add more details',
            })}
          </InlineText>
        }
        labelOutsideButton={(isOpen) => {
          if (isOpen || !hasAnyContent) {
            return null;
          }

          return (
            <InlineText color="secondary" size="200">
              {photosSummary} &bull; {contextSummary}
            </InlineText>
          );
        }}
      >
        {photosAndContextContent}
      </FoldableSection>
    </Stack>
  );
};

const ContextInput = ({
  question,
  action,
  label,
  currentContext,
  type,
  isInvalid,
}: {
  question?: ConditionFormQuestion;
  action?: { id: string };
  label: ReactNode;
  currentContext: string | null | undefined;
  type: ArticleConditionFormType;
  isInvalid?: boolean;
}) => {
  const { t } = useLingui();

  const { request, article, initialConditionFormData, qualityControlFormData } =
    useArticleContext();

  const conditionFormData =
    type === 'initial-condition' ? initialConditionFormData : qualityControlFormData;

  const [context, setContext] = useState<string>(currentContext ?? '');

  const { mutateAsync: updateArticleCondition } = useUpdateArticleCondition({
    requestId: request.id,
    articleId: article.id,
    conditionForm: conditionFormData?.conditionForm,
  });

  return (
    <TextArea
      label={label}
      placeholder={t({
        id: 'requests.new.articles.drawer.conditions-form.context.placeholder',
        message: 'Add some context...',
      })}
      onChange={(event) => setContext(event.target.value)}
      value={context}
      onBlur={() => {
        setContext(context.trim());

        updateArticleCondition({
          context: context.trim(),
          ...(question ? { questionId: question.id } : { articleActionId: action?.id }),
        }).catch(() => setContext(currentContext ?? ''));
      }}
      isInvalid={isInvalid}
    />
  );
};

function hasQuestionBeenFullyAnsweredWithNoDefects({
  question,
  action,
  articleDefects,
  articleConditionForm,
}: {
  question?: ConditionFormQuestion;
  action?: { id: string };
  articleDefects: ArticleDefectWithRelations[];
  articleConditionForm?: ArticleConditionForm;
}) {
  if (action) {
    return !!articleConditionForm?.articleConditions.find(
      (articleCondition) => articleCondition.articleActionId === action.id
    )?.answerData?.yesNoRadioValue;
  }

  if (!question) {
    return;
  }

  const articleConditions = articleConditionForm?.articleConditions.filter(
    (articleCondition) => articleCondition.questionId === question.id
  );

  if (!articleConditions?.length) {
    return false;
  }

  if (
    articleConditions?.some((condition) =>
      articleDefects.some((defect) => defect.articleConditionId === condition.id)
    )
  ) {
    return false;
  }

  if (question.type === 'text-and-select') {
    const answerData = articleConditions?.[0]?.answerData;

    if (!answerData?.text || !answerData?.select) {
      return false;
    }
  }

  if (question.type === 'multi-quantifiable-select') {
    if (articleConditions?.[0].answerId === null) {
      return false;
    }
  }

  const subQuestion = getSubQuestion({ question, articleConditionForm });

  if (subQuestion) {
    return hasQuestionBeenFullyAnsweredWithNoDefects({
      question: subQuestion,
      articleDefects,
      articleConditionForm,
    });
  }

  return true;
}
